<template>
  <v-ons-page class="ja-content-frame-select page">
    <common-header>
      <template #header>
        <span class="header-title"> フレームを選択する </span>
      </template>
    </common-header>
    <div class="content-frame-select-wrapper">
      <!-- 簡易プレビュー -->
      <ja-content-preview
        class="preview-area"
        :processNum="CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM"
        :templateId="templateId"
        :contentMaterialList="contentMaterialList"
        :contentCaption="contentCaption"
        :contentCaptionColor="contentCaptionColor"
        :contentFrame="selectedContentFrame"
      />
      <div class="content-frame-select-area">
        <div v-if="isLoadingDone" class="frames">
          <ja-content-frame-cell
            v-for="(contentFrame, index) in contentFrames"
            :key="contentFrame.frameId"
            :contentFrame="contentFrame"
            :isSelectedContentFrame="contentFrame.frameId === selectedContentFrame.frameId"
            :selectContentFrame="onSelectContentFrame"
            :class="[{ 'first-frame': index === 0 }, { 'second-frame': index === 1 }]"
          />
          <div class="empty-frame" v-for="i in emptyFrameNum" :key="-i"></div>
        </div>
        <progress-circular v-else> </progress-circular>
      </div>
    </div>
    <common-footer class="common-footer d-flex">
      <template #footer>
        <v-ons-row class="footer-button-area">
          <small-button
            class="footer-button footer-back-button"
            title="キャンセル"
            :disabled="false"
            @click="() => moveBackButton(router)"
          />
          <small-button
            class="footer-button footer-next-button"
            title="このフレームにする"
            @click="submitContentFrame"
          />
        </v-ons-row>
      </template>
    </common-footer>
  </v-ons-page>
</template>

<script setup>
// 必要コンポーネントと定数のimport
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import CommonHeader from '@/components/common/CommonHeader.vue';
import CommonFooter from '@/components/common/CommonFooter.vue';
import SmallButton from '@/components/common/SmallButton.vue';
import ProgressCircular from '@/components/common/ProgressCircular.vue';
import JaContentFrameCell from '@/components/JaContent/JaContentFrameCell.vue';
import JaContentPreview from '@/components/JaContent/JaContentPreview.vue';
import { dataFetchContentFrames } from '@/composable/data_fetch_common';
import {
  CREATE_CONTENT_PROCESS_NUM,
  NONE_FRAME_TOP,
  NONE_FRAME_BOTTOM,
} from '@/composable/constant_common';
import { moveBackButton } from '@/composable/common';

// store, router
const store = useStore();
const router = useRouter();

// ローディング完了フラグ
const isLoadingDone = ref(false);

// コンテンツフレーム一覧
const contentFrames = ref([]);
// 選択済フレーム
const selectedContentFrame = ref({});
// PC表示時の一行あたりのフレーム表示数
const ROW_FRAME_NUM_FOR_PC = 5;

/**
 * テンプレートID取得
 */
const templateId = computed(() => store.getters['content/getTemplateId']);

/**
 * コンテンツのマテリアル情報取得
 */
const contentMaterialList = computed(() => store.getters['content/getContentMaterialList']);

/**
 * コンテンツのテロップ情報取得
 */
const contentCaption = computed(() => store.getters['content/getContentCaption']);

/**
 * コンテンツのテロップカラー情報取得
 */
const contentCaptionColor = computed(() => store.getters['content/getContentCaptionColor']);
const emptyFrameNum = computed(() => {
  const contentFramesLength = contentFrames.value.length;
  return (
    Math.ceil(contentFramesLength / ROW_FRAME_NUM_FOR_PC) * ROW_FRAME_NUM_FOR_PC -
    contentFramesLength
  );
});

/**
 * コンテンツフレーム取得
 */
const getContentFrames = async () => {
  isLoadingDone.value = false;
  contentFrames.value = await dataFetchContentFrames();
  // FrameId=12のフレームなしテロップ上を取り除く
  // 本来ならばサーバ実装にて修正すべき点だが,、本機能のみの修正であるためこちらで対応
  // 今後フレームが追加となる場合でも、優先して表示させるフレームは今後発生しない想定
  contentFrames.value = contentFrames.value.filter(
    (frame) => frame.frameId !== NONE_FRAME_TOP.frameId
  );
  contentFrames.value = [NONE_FRAME_BOTTOM, NONE_FRAME_TOP, ...contentFrames.value];
  isLoadingDone.value = true;
};

/**
 * フレーム選択
 */
const onSelectContentFrame = (selectContentFrame) => {
  selectedContentFrame.value = selectContentFrame;
};

/**
 * 登録コミット
 */
const submitContentFrame = () => {
  store.commit('content/setContentFrame', selectedContentFrame.value);
  moveBackButton(router);
};

onMounted(() => {
  getContentFrames();
  selectedContentFrame.value = store.getters['content/getContentFrame'];
});
</script>

<style scoped>
/* ヘッダ */
.ja-content-frame-select {
  margin: auto;
}
.header-title {
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}

/* フレーム */
.content-frame-select-wrapper {
  padding-top: 10vh;
  padding-top: 10dvh;
  padding-bottom: 10vh;
  padding-bottom: 10dvh;
  background-color: white;
}
.frames {
  margin: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.content-frame-select-area {
  padding-bottom: 10rem;
  overflow-y: auto;
  height: calc(60vh - 20rem);
}
/* フッタ */
.common-footer {
  height: 10rem;
  flex-direction: column;
}
.footer-button-area {
  height: 100%;
  width: 90%;
}
.footer-button {
  margin: auto;
  padding: 1rem 0;
  text-align: center;
}
.footer-button:hover {
  opacity: 0.8;
}
.footer-back-button {
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
  width: 30%;
}
.footer-next-button {
  box-shadow: 0rem 0.2rem #21963b;
  color: #fff;
  width: 60%;
}
.first-frame:before {
  content: 'なし\Aテロップ下';
  white-space: pre;
  color: #aaaaaa;
  font-size: 14px;
}
.first-frame {
  background-color: #f2f2f2;
  border: solid 1px #dddddd;
}
.second-frame:before {
  content: 'テロップ上\Aなし';
  white-space: pre;
  color: #aaaaaa;
  font-size: 14px;
}
.second-frame {
  background-color: #f2f2f2;
  border: solid 1px #dddddd;
}
.empty-frame {
  display: none;
  width: 15.6rem;
  aspect-ratio: 16 / 9;
}

@media screen and (min-width: 840px) {
  .frames {
    margin-inline: 0;
    justify-content: space-between;
  }

  .empty-frame {
    display: block;
  }
}
</style>
